import { useEffect, useState } from 'react';
import { api } from '@api/apiMemberArea';
import {
  IApiCurrencyString,
  IApiGroupMappingType,
  IApiTradingAccountCardActions,
} from '@api/interfaces/data-contracts';
import {
  ACCOUNT_ARCHIVING_FORM,
  ACCOUNT_HISTORY,
  DEPOSIT_FORM,
  INVESTOR_PASSWORD_FORM,
  LEVERAGE_FORM,
  MAM_INVESTOR_CANCEL_LPOA,
  MASTER_PASSWORD_FORM,
  REFILL_FORM,
  SOCIAL_TRADING_SETTINGS_INVESTOR,
  SOCIAL_TRADING_SETTINGS_MASTER,
  TRANSFER_FORM,
  WITHDRAW_FORM,
  PUSH_NOTIFICATIONS,
} from '@components-member/TradingAccounts/AccountDetails/formFields';
import { IAccountCardMenuAction, ITradingAccountCardActions } from '@components-member/TradingAccounts/interfaces';
import { getAccountCategoryType } from '@utils/getAccountCategoryType';
import { TRADING_ACCOUNT_TYPE } from 'global.enums';
import { useMobxStore } from 'store/mobx';

const ALWAYS_ALLOWED_SETTINGS = ['historyAllowed'];

const ENABLED_TRADING_ACCOUNT_ACTIONS = [
  MASTER_PASSWORD_FORM,
  INVESTOR_PASSWORD_FORM,
  LEVERAGE_FORM,
  REFILL_FORM,
  DEPOSIT_FORM,
  WITHDRAW_FORM,
  TRANSFER_FORM,
  ACCOUNT_HISTORY,
  ACCOUNT_ARCHIVING_FORM,
  PUSH_NOTIFICATIONS,
];

const DISABLED_TRADING_ACCOUNT_ACTIONS = [ACCOUNT_HISTORY];

const EXTENTEND_DISABLED_TRADING_ACCOUNT_ACTIONS = [
  REFILL_FORM,
  DEPOSIT_FORM,
  WITHDRAW_FORM,
  TRANSFER_FORM,
  ACCOUNT_HISTORY,
];

const filterActionsForCopyTradingAccounts = (
  actions: IAccountCardMenuAction[],
  groupMappingType: IApiGroupMappingType,
  investorId: number | null,
  masterId: number | null,
  isCopyTradingEnabled: boolean,
) => {
  const { isCopyTradingAccount } = getAccountCategoryType({
    groupMappingType,
    investorId,
    masterId,
  });

  if (!isCopyTradingAccount || !isCopyTradingEnabled) {
    return actions;
  }

  return setSocialTradingActions(actions, groupMappingType, investorId, masterId);
};

const setMamTradingActions = (
  actions: IAccountCardMenuAction[],
  groupMappingType: IApiGroupMappingType,
  investorId: number | null,
  masterId: number | null,
  lpoaEnabledForMamAccounts: boolean,
  isMamInvestor: boolean,
) => {
  if (!lpoaEnabledForMamAccounts) {
    return;
  }

  const { isInMamInvestorGroupMappingType } = getAccountCategoryType({
    groupMappingType,
    investorId,
    masterId,
  });

  if (!isInMamInvestorGroupMappingType) {
    return;
  }

  if (isMamInvestor) {
    actions.push(MAM_INVESTOR_CANCEL_LPOA);
    return;
  }
};

const setSocialTradingActions = (
  actions: IAccountCardMenuAction[],
  groupMappingType: IApiGroupMappingType,
  investorId: number | null,
  masterId: number | null,
) => {
  const { isMasterAccount, isInvestorAccount } = getAccountCategoryType({
    groupMappingType,
    investorId,
    masterId,
  });

  let socialTradingAction;

  if (isMasterAccount) {
    socialTradingAction = SOCIAL_TRADING_SETTINGS_MASTER;
  }

  if (isInvestorAccount) {
    socialTradingAction = SOCIAL_TRADING_SETTINGS_INVESTOR;
  }

  if (!socialTradingAction) {
    return actions;
  }

  const indexOfSocialTradingSetting = 2;

  actions.splice(indexOfSocialTradingSetting, 0, socialTradingAction);
  return actions;
};

const filterActionsAllowedByTradingAccountSettings = (
  actions: IAccountCardMenuAction[],
  accountType: string,
  settings: IApiTradingAccountCardActions,
) => {
  return actions.filter(
    (action) =>
      action.accountTypes.indexOf(accountType as TRADING_ACCOUNT_TYPE) !== -1 &&
      (ALWAYS_ALLOWED_SETTINGS.indexOf(action.settingName) !== -1 ||
        settings[action.settingName as keyof IApiTradingAccountCardActions]),
  );
};

const filterActionsByCurrency = (actions: IAccountCardMenuAction[], accountCurrency: string) => {
  if (!accountCurrency) {
    actions = actions.filter(
      (action) =>
        action.link != DEPOSIT_FORM.link && action.link != WITHDRAW_FORM.link && action.link != TRANSFER_FORM.link,
    );
  }

  return actions;
};

const getActions = (
  actions: IAccountCardMenuAction[],
  currency: IApiCurrencyString,
  accountType: string,
  settings: IApiTradingAccountCardActions,
): IAccountCardMenuAction[] => {
  actions = filterActionsByCurrency(actions, currency);
  return filterActionsAllowedByTradingAccountSettings(actions, accountType, settings);
};

const setPushSettingsActions = (actions: IAccountCardMenuAction[], showTradingPushNotificationsTab: boolean) => {
  if (showTradingPushNotificationsTab === true) {
    return actions;
  }

  return actions.filter((action) => action.settingName !== 'pushNotificationsAllowed');
};

const getEnabledActions = (
  actions: IAccountCardMenuAction[],
  groupMappingType: IApiGroupMappingType,
  investorId: number | null,
  masterId: number | null,
  accountType: string,
  currency: string,
  settings: IApiTradingAccountCardActions,
  isCopyTradingEnabled: boolean,
  lpoaEnabledForMamAccounts: boolean,
  showTradingPushNotificationsTab: boolean,
): IAccountCardMenuAction[] => {
  const { isMamInvestor } = settings;

  actions = getActions(actions, currency, accountType, settings);
  actions = filterActionsForCopyTradingAccounts(actions, groupMappingType, investorId, masterId, isCopyTradingEnabled);

  setMamTradingActions(
    actions,
    groupMappingType,
    investorId || null,
    masterId || null,
    lpoaEnabledForMamAccounts,
    isMamInvestor,
  );

  actions = setPushSettingsActions(actions, showTradingPushNotificationsTab);

  return actions;
};

export const getAccountCardDefaultActions = (): ITradingAccountCardActions => ({
  changeInvestorPasswordAllowed: false,
  changeLeverageAllowed: false,
  changeMasterPasswordAllowed: false,
  archivingAllowed: false,
  depositAllowed: false,
  withdrawalAllowed: false,
  isMamInvestor: false,
  pushNotificationsAllowed: false,
});

export const useTradingAccountActions = (
  accountId: number,
  groupMappingType: IApiGroupMappingType,
  investorId: number | null,
  masterId: number | null,
  accountType: string,
  currency: string,
  isCopyTradingEnabled: boolean,
  enabled: boolean,
  setActionProcessing?: (state: boolean) => void,
): IAccountCardMenuAction[] => {
  const {
    accountStore: { generalSettings, onboardingConfiguration },
  } = useMobxStore();
  const { isExtendedAccountActions, lpoaEnabledForMamAccounts } = generalSettings;

  const [accountSettings, setAccountSettings] = useState<ITradingAccountCardActions>(getAccountCardDefaultActions());

  useEffect(() => {
    void (async () => {
      const response = await api.ma.tradingAccountCardUser.getActions(accountId);

      setActionProcessing?.(false);
      setAccountSettings(response.data);
    })();
  }, [accountId, setActionProcessing]);

  const disabledActions = isExtendedAccountActions
    ? (EXTENTEND_DISABLED_TRADING_ACCOUNT_ACTIONS as IAccountCardMenuAction[])
    : (DISABLED_TRADING_ACCOUNT_ACTIONS as IAccountCardMenuAction[]);

  return enabled
    ? getEnabledActions(
        ENABLED_TRADING_ACCOUNT_ACTIONS as IAccountCardMenuAction[],
        groupMappingType,
        investorId,
        masterId,
        accountType,
        currency,
        accountSettings,
        isCopyTradingEnabled,
        lpoaEnabledForMamAccounts,
        onboardingConfiguration?.showTradingPushNotificationsTab ?? false,
      )
    : getActions(disabledActions, currency, accountType, accountSettings);
};
